import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../services/api.js';
import { MAP_TILES } from '../../config/index.js';
import './noc-fleet-monitoring-alarms.scss';

const BLUE = { color: '#005293' };
const POLL_MS = 5000;
const STATUS_BADGE = { Available: 'badge-available', Charging: 'badge-charging', Reserved: 'badge-reserved', Faulted: 'badge-faulted', Offline: 'badge-secondary' };
const SEV_BADGE = { Critical: 'badge-danger', Major: 'badge-warning', Minor: 'badge-info', Warning: 'badge-secondary' };
const ALARM_STATUS = { active: ['badge-danger', 'Active'], acknowledged: ['badge-warning', 'Acknowledged'], cleared: ['badge-secondary', 'Cleared'] };

const num = (v) => Number(v ?? 0).toLocaleString('en-PH');
const power = (kw) => (kw >= 1000 ? `${(kw / 1000).toFixed(1)} MW` : `${Number(kw).toFixed(1)} kW`);
function ago(iso) {
  if (!iso) return 'never';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function Pagination({ id, page, total, size, onPage, label }) {
  if (total <= size) return null;
  const pages = Math.ceil(total / size);
  return (
    <nav aria-label={label}>
      <ul className="pagination pagination-sm mb-0" id={id}>
        <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
          <a className="page-link" href="#prev" aria-label="Previous page" onClick={(e) => { e.preventDefault(); if (page > 1) onPage(page - 1); }}>&laquo;</a>
        </li>
        {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
          <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
            <a className="page-link" href={`#p${p}`} onClick={(e) => { e.preventDefault(); onPage(p); }}>{p}</a>
          </li>
        ))}
        <li className={`page-item ${page === pages ? 'disabled' : ''}`}>
          <a className="page-link" href="#next" aria-label="Next page" onClick={(e) => { e.preventDefault(); if (page < pages) onPage(page + 1); }}>&raquo;</a>
        </li>
      </ul>
    </nav>
  );
}

export default function NocFleetMonitoringAlarmsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('grid');
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('ALL');
  const [fleet, setFleet] = useState([]);
  const [fleetMeta, setFleetMeta] = useState({ total: 0 });
  const [fleetPage, setFleetPage] = useState(1);
  const [fleetSize, setFleetSize] = useState(6);
  const [alarms, setAlarms] = useState([]);
  const [alarmMeta, setAlarmMeta] = useState({ total: 0, activeBySeverity: {}, activeTotal: 0 });
  const [alarmPage, setAlarmPage] = useState(1);
  const [severity, setSeverity] = useState('ALL');
  const [markers, setMarkers] = useState([]);
  const [detail, setDetail] = useState(null);
  // Throttle a connector from the charger detail (SetChargingProfile); the outcome joins the history below.
  const [thr, setThr] = useState({ connectorIndex: '', targetAmps: '32', busy: false, msg: null });
  const sendThrottle = async () => {
    const connectorIndex = Number(thr.connectorIndex || detail.connectors[0]?.connectorIndex);
    const targetAmps = Number(thr.targetAmps);
    if (!connectorIndex || !(targetAmps >= 6)) { setThr((t) => ({ ...t, msg: { ok: false, text: 'Choose a connector and a limit of at least 6 A.' } })); return; }
    setThr((t) => ({ ...t, busy: true, msg: null }));
    try {
      const r = await api.sendThrottle(detail.id, { connectorIndex, targetAmps });
      const refreshed = await api.getNocCharger(detail.id).catch(() => null);
      if (refreshed) setDetail(refreshed);
      setThr((t) => ({ ...t, busy: false, msg: { ok: r?.replyStatus !== 'Rejected', text: `Limit of ${targetAmps} A sent to connector #${connectorIndex}: ${r?.replyStatus || r?.status || 'sent'}.` } }));
    } catch (e) {
      setThr((t) => ({ ...t, busy: false, msg: { ok: false, text: e.message } }));
    }
  };
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);
  const alarmSize = 5;

  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const inspectRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { setFleetPage(1); }, [debounced, status, fleetSize]);
  useEffect(() => { setAlarmPage(1); }, [severity]);

  // Auto-refresh every 5 s (and on demand via the refresh button).
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), POLL_MS);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let live = true;
    api.getNocSummary().then((s) => live && setSummary(s)).catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [tick]);

  useEffect(() => {
    let live = true;
    api.listNocChargers({ page: fleetPage, pageSize: fleetSize, q: debounced, status: status === 'ALL' ? '' : status })
      .then((r) => { if (live) { setFleet(r.data); setFleetMeta(r.meta); setError(''); } })
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [fleetPage, fleetSize, debounced, status, tick]);

  useEffect(() => {
    let live = true;
    api.listNocAlarms({ page: alarmPage, pageSize: alarmSize, severity: severity === 'ALL' ? '' : severity })
      .then((r) => { if (live) { setAlarms(r.data); setAlarmMeta(r.meta); } })
      .catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [alarmPage, severity, tick]);

  useEffect(() => {
    if (tab !== 'map') return undefined;
    let live = true;
    api.getNocMap().then((m) => live && setMarkers(m)).catch((e) => live && setError(e.message));
    return () => { live = false; };
  }, [tab, tick]);

  const inspect = useCallback(async (id) => {
    try {
      setDetail(await api.getNocCharger(id));
    } catch (e) {
      setError(e.message);
    }
  }, []);
  inspectRef.current = inspect;

  const control = (ocppId) => navigate(`/charger-remote-controls?charger_id=${encodeURIComponent(ocppId)}`);

  const acknowledge = async (id) => {
    try {
      await api.acknowledgeNocAlarm(id);
      setTick((x) => x + 1);
    } catch (e) {
      setError(e.message);
    }
  };

  // Map lifecycle
  useEffect(() => {
    if (tab !== 'map' || !mapEl.current) return undefined;
    const map = L.map(mapEl.current).setView([14.5503, 121.0505], 12);
    L.tileLayer(MAP_TILES.url, MAP_TILES.options).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    const t = setTimeout(() => map.invalidateSize(), 200);
    return () => { clearTimeout(t); map.remove(); mapRef.current = null; layerRef.current = null; };
  }, [tab]);

  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const bounds = [];
    markers.forEach((s) => {
      const icon = L.divIcon({
        html: `<div class="noc-marker-bubble ${s.status}"><i class="fas fa-bolt me-1"></i>${String(s.ocppId).replace(/[<>&"]/g, '')}</div>`,
        className: 'leaflet-custom-noc-marker', iconSize: [110, 26], iconAnchor: [55, 13], popupAnchor: [0, -14],
      });
      const mk = L.marker([s.latitude, s.longitude], { icon, title: s.ocppId, alt: s.ocppId }).addTo(layer);
      mk.on('click', () => inspectRef.current(s.id));
      bounds.push([s.latitude, s.longitude]);
    });
    if (bounds.length) map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
  }, [markers, tab]);

  const s = summary || {};
  const pct = (a) => (s.totalChargers ? `${Math.round((a / s.totalChargers) * 100)}%` : '0%');
  const fleetStart = fleetMeta.total === 0 ? 0 : (fleetPage - 1) * fleetSize + 1;
  const fleetEnd = Math.min(fleetPage * fleetSize, fleetMeta.total);
  const alarmStart = alarmMeta.total === 0 ? 0 : (alarmPage - 1) * alarmSize + 1;
  const alarmEnd = Math.min(alarmPage * alarmSize, alarmMeta.total);
  const by = alarmMeta.activeBySeverity || {};

  const stat = (label, id, value, valueClass, badge) => (
    <div className="col-12 col-md-6 col-lg-2 mb-3">
      <div className="card border shadow-sm h-100 bg-white">
        <div className="card-body p-3">
          <div className="text-muted small text-uppercase fw-bold">{label}</div>
          <div className="d-flex align-items-baseline justify-content-between mt-2">
            <h3 className={`mb-0 fw-bold ${valueClass}`} style={valueClass ? undefined : BLUE} id={id}>{value}</h3>
            {badge}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid py-4" id="noc-fleet-container">
      {error && <div className="alert alert-danger py-2" role="alert" id="noc-error">{error}</div>}

      <div className="row mb-4">
        {stat('Total Network', 'stat-total', num(s.totalChargers), '', <span className="badge badge-secondary"><i className="fas fa-charging-station me-1" /> Fleet</span>)}
        {stat('Online & Ready', 'stat-available', num(s.availableChargers), 'text-success', <span className="badge badge-success-soft"><i className="fas fa-check-circle me-1" /> {pct(s.availableChargers)}</span>)}
        {stat('Active Charging', 'stat-charging', num(s.chargingChargers), 'text-info', <span className="badge badge-info-soft"><i className="fas fa-bolt me-1" /> {power(s.totalKw || 0)}</span>)}
        {stat('Reserved', 'stat-reserved', num(s.reservedChargers), 'text-warning', <span className="badge badge-warning-soft"><i className="fas fa-clock me-1" /> Holds</span>)}
        {stat('Faulted / Degraded', 'stat-faulted', num(s.faultedChargers), 'text-danger', <span className="badge badge-danger-soft"><i className="fas fa-triangle-exclamation me-1" /> {pct(s.faultedChargers)}</span>)}
        {stat('Critical Alarms', 'stat-alarms', num(s.criticalAlarms), `text-danger ${s.criticalAlarms ? 'animate-pulse' : ''}`, <span className="badge badge-danger"><i className="fas fa-bell me-1" /> Unacknowledged</span>)}
      </div>

      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
        <div className="btn-group" role="group">
          <button type="button" className={`btn btn-outline-primary ${tab === 'grid' ? 'active' : ''}`} id="btn-tab-grid" onClick={() => setTab('grid')}><i className="fas fa-th-large me-1" /> Fleet Grid</button>
          <button type="button" className={`btn btn-outline-primary ${tab === 'alarms' ? 'active' : ''}`} id="btn-tab-alarms" onClick={() => setTab('alarms')}>
            <i className="fas fa-exclamation-circle me-1" /> Alarms Console <span className="badge badge-danger ms-1" id="badge-alarm-count">{alarmMeta.activeTotal}</span>
          </button>
          <button type="button" className={`btn btn-outline-primary ${tab === 'map' ? 'active' : ''}`} id="btn-tab-map" onClick={() => setTab('map')}><i className="fas fa-map-marked-alt me-1" /> Plaza Map</button>
        </div>
        <div className="d-flex flex-wrap align-items-center mt-2 mt-md-0 gap-2">
          <div className="input-group input-group-sm noc-search" style={{ width: 280 }}>
            <span className="input-group-text bg-white"><i className="fas fa-search text-muted" /></span>
            <input type="text" className="form-control" id="search-filter" aria-label="Search chargers" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${num(s.totalChargers)} chargers by OCPP ID, Site, Vendor...`} />
          </div>
          <select className="form-select form-select-sm" id="filter-status" aria-label="Filter by status" style={{ width: 140 }} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="ALL">All Statuses</option>
            <option value="Available">Available</option>
            <option value="Charging">Charging</option>
            <option value="Reserved">Reserved</option>
            <option value="Faulted">Faulted</option>
          </select>
          <button className="btn btn-sm btn-outline-info" id="btn-refresh" onClick={() => setTick((x) => x + 1)}><i className="fas fa-sync-alt me-1" /> Auto-Refresh: 5s</button>
        </div>
      </div>

      {tab === 'grid' && (
        <div id="view-fleet-grid">
          <div className="d-flex justify-content-between align-items-center mb-2 px-1">
            <span className="text-muted small">Showing <strong id="fleet-range-start">{fleetStart}</strong>–<strong id="fleet-range-end">{fleetEnd}</strong> of <strong id="fleet-count">{fleetMeta.total}</strong> chargers</span>
          </div>
          <div className="row" id="fleet-cards-row">
            {fleet.length === 0 && <div className="col-12"><div className="alert alert-info bg-white border p-4 text-center">No chargers match your active filter criteria.</div></div>}
            {fleet.map((c) => (
              <div className="col-12 col-md-6 col-lg-4 mb-3" key={c.id}>
                <div className={`card border-0 shadow-sm station-card status-${c.status} h-100`} data-ocpp={c.ocppId}>
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <h6 className="fw-bold mb-0" style={BLUE}>{c.ocppId}</h6>
                        <small className="text-muted"><i className="fas fa-map-marker-alt me-1" />{c.site || 'Unassigned site'}</small>
                      </div>
                      <span className={`badge ${STATUS_BADGE[c.status]} px-2 py-1`}>{c.status}</span>
                    </div>
                    <div className="bg-light rounded p-2 my-2 small d-flex justify-content-between text-muted border">
                      <span><strong>{c.vendor || 'Unknown'}</strong> {c.model}</span>
                      <span>FW: <code>{c.firmware || 'n/a'}</code></span>
                    </div>
                    <div className="small text-muted d-flex flex-wrap gap-1">
                      <span className="badge badge-secondary">OCPP {c.ocppVersion}</span>
                      <span className={`badge ${c.tier === 'Certified' ? 'badge-available' : c.tier === 'Compatible' ? 'badge-reserved' : 'badge-faulted'}`}>{c.tier}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
                      <span className="small text-muted"><i className="fas fa-heartbeat text-danger me-1" /> HB: {ago(c.lastHeartbeat)}</span>
                      <div>
                        <button className="btn btn-xs btn-outline-primary me-1 btn-inspect-station" onClick={() => inspect(c.id)}>Inspect</button>
                        <button className="btn btn-xs btn-primary btn-remote-station" onClick={() => control(c.ocppId)}><i className="fas fa-terminal me-1" />Control</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="card p-2 shadow-sm border bg-white d-flex flex-row justify-content-between align-items-center mt-2">
            <div className="d-flex align-items-center">
              <span className="text-muted small me-2">Per page:</span>
              <select id="select-fleet-page-size" className="form-select form-select-sm" aria-label="Chargers per page" style={{ width: 70 }} value={fleetSize} onChange={(e) => setFleetSize(Number(e.target.value))}>
                <option value="6">6</option>
                <option value="12">12</option>
                <option value="24">24</option>
              </select>
            </div>
            <Pagination id="fleet-pagination" page={fleetPage} total={fleetMeta.total} size={fleetSize} onPage={setFleetPage} label="Fleet pagination" />
          </div>
        </div>
      )}

      {tab === 'alarms' && (
        <div id="view-alarms-console">
          <div className="card border shadow-sm bg-white">
            <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center py-3 gap-2">
              <h5 className="mb-0 fw-bold" style={BLUE}><i className="fas fa-bell text-warning me-2" />Active Hardware Alarms &amp; Fault Signals</h5>
              <div className="btn-group btn-group-sm">
                <button className={`btn btn-outline-secondary btn-sev-filter ${severity === 'ALL' ? 'active' : ''}`} data-severity-filter="ALL" onClick={() => setSeverity('ALL')}>All ({alarmMeta.activeTotal})</button>
                <button className={`btn btn-outline-danger btn-sev-filter ${severity === 'Critical' ? 'active' : ''}`} data-severity-filter="Critical" onClick={() => setSeverity('Critical')}>Critical ({by.Critical || 0})</button>
                <button className={`btn btn-outline-warning btn-sev-filter ${severity === 'Major' ? 'active' : ''}`} data-severity-filter="Major" onClick={() => setSeverity('Major')}>Major ({by.Major || 0})</button>
                <button className={`btn btn-outline-info btn-sev-filter ${severity === 'Minor' ? 'active' : ''}`} data-severity-filter="Minor" onClick={() => setSeverity('Minor')}>Minor ({by.Minor || 0})</button>
              </div>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr className="text-muted small text-uppercase">
                      <th>Severity</th>
                      <th>Alarm Code</th>
                      <th>Station / Connector</th>
                      <th>Error Code / Diagnostic</th>
                      <th>Timestamp</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody id="alarms-table-body">
                    {alarms.length === 0 && <tr><td colSpan="7" className="text-center p-4 text-muted">No alarms match the selected severity tier.</td></tr>}
                    {alarms.map((a) => {
                      const [cls, label] = ALARM_STATUS[a.status] || ALARM_STATUS.cleared;
                      return (
                        <tr key={a.id} data-alarm-id={a.id}>
                          <td><span className={`badge ${SEV_BADGE[a.severity]} px-2`}>{a.severity}</span></td>
                          <td className="fw-bold text-dark font-monospace">{a.alarmCode}</td>
                          <td>
                            <span className="fw-bold" style={BLUE}>{a.ocppId}</span>
                            {a.connectorIndex ? <span className="badge badge-secondary ms-1">Gun #{a.connectorIndex}</span> : null}
                          </td>
                          <td>
                            <code className="text-danger fw-bold">{a.errorCode}</code>
                            <div className="small text-muted text-truncate" style={{ maxWidth: 280 }} title={a.description}>{a.description}</div>
                          </td>
                          <td className="small text-muted">{ago(a.raisedAt)}</td>
                          <td><span className={`badge ${cls}`}>{label}</span></td>
                          <td className="text-end">
                            <button className="btn btn-sm btn-outline-warning me-1 btn-ack-alarm" disabled={a.status !== 'active'} onClick={() => acknowledge(a.id)}>Ack</button>
                            <button className="btn btn-sm btn-outline-info btn-create-ticket" onClick={() => navigate(`/maintenance-tickets-diagnostics?alarm_code=${encodeURIComponent(a.alarmCode)}`)}>Ticket</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer bg-white d-flex justify-content-between align-items-center py-2 px-3">
              <span className="text-muted small">Showing <strong id="alarms-range-start">{alarmStart}</strong>–<strong id="alarms-range-end">{alarmEnd}</strong> of <strong id="alarms-count">{alarmMeta.total}</strong> alarms</span>
              <Pagination id="alarms-pagination" page={alarmPage} total={alarmMeta.total} size={alarmSize} onPage={setAlarmPage} label="Alarms pagination" />
            </div>
          </div>
        </div>
      )}

      {tab === 'map' && (
        <div id="view-map">
          <div className="card border shadow-sm bg-white">
            <div className="card-header bg-white d-flex justify-content-between align-items-center py-2 px-3">
              <span className="fw-bold" style={BLUE}><i className="fas fa-satellite text-primary me-2" />Interactive Geographic Telemetry Map — Metro Manila Network</span>
              <span className="badge badge-success small"><i className="fas fa-signal me-1" />Refreshes automatically</span>
            </div>
            <div className="card-body p-0">
              <div id="noc-fleet-map" ref={mapEl} style={{ height: 540, width: '100%', background: '#f8fafc', borderRadius: '0 0 12px 12px' }} />
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="modal d-block noc-backdrop" id="modal-station-detail" tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="detail-station-title">
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
            <div className="modal-content shadow-lg bg-white">
              <div className="modal-header">
                <h5 className="modal-title fw-bold" id="detail-station-title" style={BLUE}>
                  <i className="fas fa-charging-station text-primary me-2" />{detail.ocppId} — {detail.vendor} {detail.model}
                </h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={() => setDetail(null)} />
              </div>
              <div className="modal-body" id="detail-modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="text-muted small">Operational Status</label>
                    <div><span className={`badge ${STATUS_BADGE[detail.status]} px-3 py-1`}>{detail.status}</span></div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="text-muted small">Active Power Output</label>
                    <div className="h5 fw-bold mb-0" style={BLUE}>{detail.powerKw} kW</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="text-muted small">Charging Plaza Location</label>
                    <div className="fw-bold text-dark">{detail.site || 'Unassigned site'}</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="text-muted small">Firmware &amp; Protocol</label>
                    <div className="text-dark"><code>{detail.firmware || 'n/a'}</code> (OCPP {detail.ocppVersion})</div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="text-muted small">Compatibility Tier</label>
                    <div>
                      <span className="badge badge-secondary">{detail.tier}</span>
                      {detail.tierDowngraded && <span className="badge badge-faulted ms-1">Auto-downgraded</span>}
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="text-muted small">Last Heartbeat</label>
                    <div className="text-dark">{ago(detail.lastHeartbeat)}</div>
                  </div>
                </div>
                <h6 className="fw-bold mt-2" style={BLUE}>Connector Diagnostics</h6>
                <div className="table-responsive">
                  <table className="table table-sm" id="detail-connectors">
                    <thead><tr className="text-muted small text-uppercase"><th>Gun</th><th>Type</th><th>Status</th><th>Rating</th><th>Live current / power</th></tr></thead>
                    <tbody>
                      {detail.connectors.length === 0 && <tr><td colSpan="5" className="text-muted">No connectors registered.</td></tr>}
                      {detail.connectors.map((k) => {
                        const t = detail.telemetry.find((x) => x.connectorIndex === k.connectorIndex);
                        return (
                          <tr key={k.connectorIndex}>
                            <td>#{k.connectorIndex}</td>
                            <td>{k.type}</td>
                            <td><span className={`badge ${STATUS_BADGE[k.status === 'Available' ? 'Available' : k.status === 'Faulted' ? 'Faulted' : k.status === 'Reserved' ? 'Reserved' : 'Charging']}`}>{k.status}</span></td>
                            <td>{k.maxPowerKw} kW{k.maxAmperageA ? ` / ${k.maxAmperageA} A` : ''}</td>
                            <td>{t ? `${t.currentA ?? '–'} A / ${t.powerKw} kW` : '–'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <h6 className="fw-bold mt-3" style={BLUE}>Recent Alarms</h6>
                {detail.recentAlarms.length === 0 ? <p className="text-muted small mb-0">No alarms recorded for this charger.</p> : (
                  <ul className="list-unstyled small mb-0">
                    {detail.recentAlarms.map((a) => (
                      <li key={a.id} className="mb-1"><span className={`badge ${SEV_BADGE[a.severity]} me-1`}>{a.severity}</span><code>{a.errorCode}</code> — {a.description} <span className="text-muted">({ago(a.raisedAt)})</span></li>
                    ))}
                  </ul>
                )}
                <h6 className="fw-bold mt-3" style={BLUE}>Throttle connector</h6>
                <div className="d-flex flex-wrap align-items-end gap-2" id="throttle-control">
                  <div>
                    <label className="form-label small fw-bold mb-1" htmlFor="throttle-connector">Connector</label>
                    <select className="form-select form-select-sm" id="throttle-connector" value={thr.connectorIndex || detail.connectors[0]?.connectorIndex || ''} onChange={(e) => setThr((t) => ({ ...t, connectorIndex: e.target.value }))}>
                      {detail.connectors.map((k) => <option key={k.connectorIndex} value={k.connectorIndex}>#{k.connectorIndex} {k.type}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="form-label small fw-bold mb-1" htmlFor="throttle-amps">Limit (A)</label>
                    <input type="number" min="6" max="1000" className="form-control form-control-sm" id="throttle-amps" style={{ width: 110 }} value={thr.targetAmps} onChange={(e) => setThr((t) => ({ ...t, targetAmps: e.target.value }))} />
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-primary" id="btn-send-throttle" disabled={thr.busy || detail.connectors.length === 0} onClick={sendThrottle}>
                    {thr.busy ? 'Sending…' : 'Send limit'}
                  </button>
                </div>
                {thr.msg && <div className={`small mt-2 ${thr.msg.ok ? 'text-success' : 'text-danger'}`} role="status" id="throttle-result">{thr.msg.text}</div>}
                <p className="text-muted small mt-1 mb-0">Sends an OCPP charging-profile limit. The charger&apos;s next meter readings are checked against it.</p>
                {detail.throttleCommands.length > 0 && (
                  <>
                    <h6 className="fw-bold mt-3" style={BLUE}>Throttling Commands (closed-loop verification)</h6>
                    <ul className="list-unstyled small mb-0">
                      {detail.throttleCommands.map((t) => (
                        <li key={t.id}>Connector #{t.connectorIndex} → {t.targetAmps} A: <strong>{t.status.replace('_', ' ')}</strong>{t.observedAmps != null ? ` (observed ${t.observedAmps} A)` : ''}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" id="btn-modal-open-remote" className="btn btn-primary" onClick={() => control(detail.ocppId)}>
                  <i className="fas fa-terminal me-1" /> Open Remote Controls
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setDetail(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
